import React, { useState } from 'react';
import { ArrowRight, Star, Shield, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { businessInfo } from '../data/mock';
import BookingCalendar from './BookingCalendar';

const HeroSection = () => {
  const [isBookingOpen, setIsBookingOpen] = useState(false);

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img 
          src="/hero-massage.jpg"
          alt="Relaxing massage experience"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1a1a18]/90 via-[#1a1a18]/75 to-[#1a1a18]/40" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-32">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-10">
            {/* Badge */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="inline-flex items-center gap-3 border border-luxury-gold/30 text-luxury-gold px-5 py-2.5 rounded-full text-xs uppercase tracking-[0.2em] font-medium">
                <Star size={14} className="fill-luxury-gold text-luxury-gold" />
                Trusted by 1000+ Clients
              </div>
              <div className="inline-flex items-center gap-2.5 border border-luxury-gold/30 px-4 py-1.5 rounded-full" data-testid="hero-tripadvisor-badge">
                <img
                  src="/tripadvisor-2026.png"
                  alt="Tripadvisor Travellers' Choice 2026"
                  className="w-7 h-9 object-contain"
                />
                <span className="text-luxury-gold text-xs uppercase tracking-[0.2em] font-medium">Travellers&rsquo; Choice 2026</span>
              </div>
            </div>

            {/* Heading */}
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-medium text-white leading-[1.1]">
              <span className="italic text-white/90">Relax.</span>
              <span className="block text-luxury-gold mt-2 italic">Rejuvenate.</span>
              <span className="block text-warm-200 mt-2">Revive.</span>
            </h1>

            <p className="text-lg text-white/60 max-w-lg leading-relaxed font-light">
              Professional therapeutic massage by qualified therapists with over {businessInfo.experience} of experience. 
              Deep tissue, Lomi Lomi, and bespoke relaxation.
            </p>

            {/* Trust */}
            <div className="flex flex-wrap gap-8 text-xs text-white/50 uppercase tracking-[0.15em]">
              <div className="flex items-center gap-2">
                <Shield size={14} strokeWidth={1.5} className="text-luxury-gold" />
                Health Insurance Accepted
              </div>
              <div className="flex items-center gap-2">
                <Clock size={14} strokeWidth={1.5} className="text-luxury-gold" />
                Open 7 Days
              </div>
            </div>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button
                onClick={() => setIsBookingOpen(true)}
                className="bg-luxury-gold hover:bg-luxury-gold/90 text-luxury-green px-10 py-7 text-xs uppercase tracking-[0.25em] font-semibold rounded-full transition-all duration-500 hover:shadow-2xl hover:shadow-luxury-gold/20 hover:-translate-y-0.5 group"
                data-testid="hero-book-btn"
              >
                Book Your Experience
                <ArrowRight size={16} className="ml-3 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
              </Button>
              <Button
                onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
                variant="ghost"
                className="text-white/70 hover:text-white hover:bg-white/5 px-8 py-7 text-xs uppercase tracking-[0.2em] rounded-full border border-white/15 hover:border-white/30 transition-all duration-500"
              >
                View Treatments
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-8 pt-10 border-t border-white/10">
              <div>
                <div className="font-display text-3xl text-luxury-gold">14+</div>
                <div className="text-[10px] text-white/40 uppercase tracking-[0.2em] mt-1">Years</div>
              </div>
              <div>
                <div className="font-display text-3xl text-luxury-gold">2</div>
                <div className="text-[10px] text-white/40 uppercase tracking-[0.2em] mt-1">Locations</div>
              </div>
              <div>
                <div className="font-display text-3xl text-luxury-gold flex items-center gap-1">5<Star size={16} className="fill-luxury-gold text-luxury-gold" /></div>
                <div className="text-[10px] text-white/40 uppercase tracking-[0.2em] mt-1">Rating</div>
              </div>
            </div>
          </div>

          {/* Right side - Floating Cards */}
          <div className="relative hidden lg:block">
            <div className="absolute top-10 right-10 bg-white/10 backdrop-blur-xl rounded-3xl border border-white/10 p-8 shadow-2xl">
              <div className="text-xs text-white/50 uppercase tracking-[0.2em]">From</div>
              <div className="font-display text-4xl text-white mt-1">€80</div>
              <div className="text-xs text-white/40 mt-1">30 minute session</div>
            </div>

            <div className="absolute bottom-20 left-0 bg-white/10 backdrop-blur-xl rounded-3xl border border-white/10 p-8 shadow-2xl">
              <div className="flex items-center gap-1 mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={`hero-star-${i}`} size={16} className="fill-luxury-gold text-luxury-gold" />
                ))}
              </div>
              <div className="text-sm text-white font-medium">Exceptional</div>
              <div className="text-xs text-white/40 mt-1">Google & TripAdvisor</div>
            </div>
          </div>
        </div>
      </div>

      <BookingCalendar isOpen={isBookingOpen} onClose={() => setIsBookingOpen(false)} />
    </section>
  );
};

export default HeroSection;
